import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import InputField from "./TextInputComponent";
import Box from "./BoxComponent";
import UserSvg from "../assets/icons/user.svg";
import keySvg from "../assets/icons/key.svg";
import CustomButton from "./CustomButton";
import lootka from "../assets/images/newLootkaFont.webp";
import { ReactComponent as Close } from "../assets/icons/x-circle.svg";
import RegisterChoise from "./RegisterChoise";
import { loginUser } from "../utils/authService";
import useUserStore from "../store/userStore";
import { toast } from "react-toastify";

const loginSchema = z.object({
  username_or_email: z
    .string({ required_error: "شماره موبایل الزامی است" })
    .min(3, { message: "ورودی معتبر نیست" }),
  password: z
    .string({ required_error: "رمز عبور مورد نیاز است" })
    .min(8, { message: "رمز عبور باید حداقل 8 کاراکتر باشد" }),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginProps {
  onClose?: () => void;
  switchToSignup?: () => void;
}

const Login = ({ onClose, switchToSignup }: LoginProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);

      const response = await loginUser(data);
      const payload = response.data ?? response;
      const userData = payload.user ?? payload;
      const token =
        payload.session_token ??
        payload.access_token ??
        payload.access ??
        payload.token ??
        response.session_token ??
        response.access_token ??
        response.access ??
        response.token ??
        null;

      if (!userData?.user_id) {
        throw new Error("Invalid login response.");
      }

      setUser(
        {
          userId: userData.user_id,
          username: userData.username,
          email: userData.email,
          fullName: userData.full_name,
          image: userData.image ?? null,
        },
        typeof token === "string" ? token : null
      );

      onClose?.();

      toast.success("با موفقیت وارد شدید", {
        className: "toast font-myYekanFaNumRegular",
        hideProgressBar: true,
      });

    } catch (error: any) {
      if (error.response?.status === 401) {
        toast.error("اطلاعات وارد شده صحیح نیست", {
          className: "toast",
        });
      } else {
        toast.error("خطایی رخ داد", {
          className: "toast",
          hideProgressBar: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box heightClass="h-[500px] p-2 relative">
      <button
        onClick={onClose}
        className="absolute left-6 top-6 z-50 text-2xl text-gray-700 hover:text-gray-500 desktop:hidden"
      >
        <Close />
      </button>

      <img
        src={lootka}
        alt="لوتکا"
        className="absolute top-4 w-32 object-contain tablet:w-40"
      />

      <RegisterChoise switchToSignup={switchToSignup} active="login" />

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mt-[14%] flex w-full max-w-[320px] flex-col items-center px-4 tablet:px-0"
      >
        <InputField
          type="text"
          placeholder="شماره موبایل"
          error={errors.username_or_email?.message}
          register={register}
          name="username_or_email"
          iconsrc={UserSvg}
        />

        <InputField
          type="password"
          placeholder="رمز عبور"
          error={errors.password?.message}
          register={register}
          name="password"
          iconsrc={keySvg}
        />

        <div className="flex flex-col w-full gap-2">
          <CustomButton
            type="submit"
            disabled={isLoading}
            text={isLoading ? "در حال ورود..." : "ورود به لوتکا"}
            className="mt-2 bg-[#038545] text-white text-sm w-full rounded-lg justify-center font-myYekanFaNumRegular"
          />
          <div className="flex flex-col mx-auto">
            <button
              type="button"
              onClick={switchToSignup}
              className="text-sm font-myYekanFaNumRegular"
            >
              حساب کاربری ندارید؟ ثبت‌نام
            </button>
          </div>
        </div>
      </form>
    </Box>
  );
};

export default Login;
